var Type = /*#__PURE__*/function (Type) {
  Type["Array"] = "array";
  Type["AsyncFunction"] = "asyncfunction";
  Type["Error"] = "error";
  Type["Function"] = "function";
  Type["Null"] = "null";
  Type["Number"] = "number";
  Type["Object"] = "object";
  Type["Promise"] = "promise";
  Type["Regexp"] = "regexp";
  Type["String"] = "string";
  Type["Symbol"] = "symbol";
  Type["Undefined"] = "undefined";
  return Type;
}(Type || {});
/**
 * @remarks {@link typeOf typeOf()} returns the type of the given value.
 *
 * @param {unknown} value The value to check.
 * @returns array | function | null | number | object | promise | regexp | string | symbol | undefined
 *
 * @example
 *
 * ```ts
 * typeOf(''); // string
 * ```
 */
const typeOf = value => Object.prototype.toString.call(value).slice(8, -1).toLowerCase();

/**
 * @remarks {@link isArray isArray()} checks if a given value is an array.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isArray([]); // true
 * ```
 */
const isArray = value => typeOf(value) === Type.Array;

/**
 * @remarks {@link isAsyncFunction isAsyncFunction()} checks if a given value is an async function.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isAsyncFunction(() => {}); // false
 * isAsyncFunction(async () => {}); // true
 * ```
 */
const isAsyncFunction = value => typeOf(value) === Type.AsyncFunction;

/**
 * @remarks {@link isFunction isFunction()} checks if a given value is a function.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isFunction(() => {}); // true
 * isFunction(async () => {}); // true
 * ```
 */
const isFunction = value => typeOf(value).includes(Type.Function);

/**
 * @remarks {@link isError isError()} checks if a given value is an error.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isError(new Error(':())); // true
 * ```
 */
const isError = value => typeOf(value).includes(Type.Error);

/**
 * @remarks {@link isNull isNull()} checks if a given value is null.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isNull(null); // true
 * ```
 */
const isNull = value => typeOf(value) === Type.Null;

/**
 * @remarks {@link isNumber isNumber()} checks if a given value is a number.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isNumber(1); // true
 * ```
 */
const isNumber = value => typeOf(value) === Type.Number;

/**
 * @remarks {@link isObject isObject()} checks if a given value is an object.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isObject({}); // true
 * ```
 */
const isObject = value => typeOf(value) === Type.Object;

/**
 * @remarks {@link isPromise isPromise()} checks if a given value is a promise.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isPromise(Promise.resolve()); // true
 * ```
 */
const isPromise = value => typeOf(value) === Type.Promise;

/**
 * @remarks {@link isRegexp isRegexp()} checks if a given value is a regular expression.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isRegexp(/\/); // true
 * ```
 */
const isRegexp = value => typeOf(value) === Type.Regexp;

/**
 * @remarks {@link isString isString()} checks if a given value is a string.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isString(''); // true
 * ```
 */
const isString = value => typeOf(value) === Type.String;

/**
 * @remarks {@link isSymbol isSymbol()} checks if a given value is a symbol.
 *
 * @param {unknown} value The value to check.
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isSymbol(new Symbol()); // true
 * ```
 */
const isSymbol = value => typeOf(value) === Type.Symbol;

/**
 * @remarks {@link isUndefined isUndefined()} checks if a given value is undefined.
 *
 * @param {unknown} value The value to check.
 *
 * @returns boolean
 *
 * @example
 *
 * ```ts
 * isUndefined(undefined); // true
 * ```
 */
const isUndefined = value => typeOf(value) === Type.Undefined;
/**
 * @remarks {@link isNil isNil()} checks if a given value is undefined or null.
 *
 * @param value - The value to check.
 *
 * @returns Whether or not the value is nil.
 *
 * @example
 *
 * ```ts
 * isNil(undefined); // true
 * isNil(null); // true
 * isNil('foo'); // false
 * isNil(123); // false
 * ```
 */
const isNil = value => value == undefined;

/**
 * @remarks {@link isNotNil isNotNil()} checks if a given value is undefined or null.
 *
 * @param value - The value to check.
 *
 * @returns Whether or not the value is nil.
 *
 * @example
 *
 * ```ts
 * isNotNil(undefined); // false
 * isNotNil(null); // false
 * isNotNil('foo'); // true
 * isNotNil(123); // true
 * ```
 */
const isNotNil = value => value != null;

export { isNull as a, isString as b, isNil as c, isArray as d, isAsyncFunction as e, isError as f, isFunction as g, isNotNil as h, isUndefined as i, isNumber as j, isObject as k, isPromise as l, isRegexp as m, isSymbol as n, typeOf as t };
